import { useMemo, type ReactNode } from "react";
import { CircularProgress } from "@mui/material";

import type { ParameterTimeSeriesConfiguration, ParametersLocation } from "@/lib/api";
import type { TimeSeriesConfig } from "@/components/graph/graph.types";
import type { TimeRangeDimensionValue } from "@/lib/dimensions/time-range-dimension";
import type { TimeSeriesParameterOption } from "@/features/shared/widgets/map/time-series/time-series-parameter.types";
import { useObservedLocationsQuery } from "@/features/shared/widgets/observations-map/hooks/queries";
import { locationSupportsAnyConfiguredParameter } from "@/features/shared/widgets/observations-map/marker-display-state";
import { useTimeSeriesWidgetChartData } from "@/features/shared/widgets/time-series-widget/use-time-series-widget-chart-data";
import TimeseriesGraphs from "@/features/dashboard/components/timeseries/timeseries-graphs";
import {
  TimeSeriesChartPreviewProvider as SharedTimeSeriesChartPreviewProvider,
  TimeSeriesChartPreviewToolbar,
  TimeSeriesChartPreviewBody,
} from "@/features/widget-forms/forms/map-widget-editor/time-series-chart-preview/time-series-chart-preview";
import sharedStyles from "@/features/widget-forms/forms/map-widget-editor/time-series-chart-preview/time-series-chart-preview.module.css";
import ScrollArea from "@/components/scroll-area/scroll-area";
import Stack from "@/components/stack/stack";

// Re-export the shared toolbar/body so the widget dialog wiring stays unchanged.
export { TimeSeriesChartPreviewToolbar, TimeSeriesChartPreviewBody };

type TimeSeriesChartPreviewProviderProps = {
  /** The single time-series group being edited (live draft), or `null` until valid. */
  config: ParameterTimeSeriesConfiguration | null;
  /** Catalog parameter options for the widget (drives labels/units/viz type). */
  parameters: TimeSeriesParameterOption[];
  /**
   * `parameterId -> modelId` for model-sourced params (from the standalone
   * time-series catalogue). Omitted for obs-map, whose params are all observed.
   */
  parameterModelIndex?: Record<string, string>;
  children: ReactNode;
};

/**
 * Obs-map adapter over the shared chart-preview scaffold: it lists the observed
 * stations that support at least one configured parameter and renders the chart
 * from the same per-station observed pipeline as the dashboard panel.
 */
export function TimeSeriesChartPreviewProvider({
  config,
  parameters,
  parameterModelIndex = {},
  children,
}: TimeSeriesChartPreviewProviderProps) {
  const locationsQuery = useObservedLocationsQuery();

  const configuredParameterIds = useMemo(
    () => config?.series.map((series) => series.parameter_id) ?? [],
    [config],
  );

  const supportingStations = useMemo<ParametersLocation[]>(() => {
    if (!config || configuredParameterIds.length === 0) return [];
    return (locationsQuery.data ?? []).filter((location) =>
      locationSupportsAnyConfiguredParameter(location, configuredParameterIds),
    );
  }, [config, configuredParameterIds, locationsQuery.data]);

  const timeSeriesConfigurations = useMemo(() => (config ? [config] : []), [config]);

  return (
    <SharedTimeSeriesChartPreviewProvider
      config={config}
      parameters={parameters}
      parameterModelIndex={parameterModelIndex}
      stations={supportingStations}
      stationsLoading={locationsQuery.isPending}
      renderChart={(station, { timeSeriesConfigs, currentTime }) => (
        <ScrollArea viewportProps={{ className: sharedStyles.scrollViewport }}>
          <StationChart
            location={station}
            timeSeriesConfigs={timeSeriesConfigs}
            timeSeriesConfigurations={timeSeriesConfigurations}
            parameterModelIndex={parameterModelIndex}
            currentTime={currentTime}
          />
        </ScrollArea>
      )}
    >
      {children}
    </SharedTimeSeriesChartPreviewProvider>
  );
}

type StationChartProps = {
  location: ParametersLocation;
  timeSeriesConfigs: TimeSeriesConfig[];
  timeSeriesConfigurations: ParameterTimeSeriesConfiguration[];
  parameterModelIndex: Record<string, string>;
  currentTime: TimeRangeDimensionValue;
};

/**
 * Isolated so {@link useTimeSeriesWidgetChartData} always runs with a concrete
 * station (the parent only mounts this once one is selected).
 */
function StationChart({
  location,
  timeSeriesConfigs,
  timeSeriesConfigurations,
  parameterModelIndex,
  currentTime,
}: StationChartProps) {
  const { chartData, pending } = useTimeSeriesWidgetChartData({
    station: location,
    timeSeriesConfigs,
    timeSeriesConfigurations,
    parameterModelIndex,
  });

  if (pending) {
    return (
      <Stack alignItems="center" justifyContent="center">
        <CircularProgress size={24} color="inherit" />
      </Stack>
    );
  }

  return (
    <TimeseriesGraphs
      timeSeriesConfigs={timeSeriesConfigs}
      data={chartData}
      currentTime={currentTime}
    />
  );
}
